import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useDebouncedValue } from "../../../../hooks/useDebounce";

/* ------------ Small UI helpers ------------ */
const ListSkeleton = ({ rows = 3 }) => (
  <div className="mt-3 space-y-3 pr-4">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="animate-pulse rounded-lg bg-white p-3 shadow">
        <div className="mb-2 h-4 w-1/3 rounded bg-gray-200/80" />
        <div className="space-y-2">
          <div className="h-4 w-5/6 rounded bg-gray-200/80" />
          <div className="h-4 w-3/5 rounded bg-gray-200/80" />
        </div>
      </div>
    ))}
  </div>
);

/* ------------ Motion variants ------------ */
const backdrop = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.12 } },
};

const sheet = {
  hidden: { y: "100%" },
  show: {
    y: 0,
    transition: { type: "spring", stiffness: 420, damping: 40, mass: 0.8 },
  },
  exit: { y: "100%", transition: { duration: 0.18 } },
};

/* ------------ Component ------------ */
const ProcedurePopup = ({ data, category, onClose, serviceLoading }) => {
  const navigate = useNavigate();
  const [procedures, setProcedures] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debounced = useDebouncedValue(searchTerm, 250);
  const sheetRef = useRef(null);

  /* Đồng bộ dữ liệu từ props */
  useEffect(() => {
    setProcedures(Array.isArray(data) ? data : []);
  }, [data]);

  /* Khóa cuộn nền khi popup mở */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  /* Đóng bằng phím ESC */
  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  /* Lọc client-side (debounced) */
  const filteredProcedures = useMemo(() => {
    const q = (debounced || "").trim().toLowerCase();
    if (!q) return procedures;
    return procedures.filter((p) => (p.ten || "").toLowerCase().includes(q));
  }, [debounced, procedures]);

  /* Click card → đi đến chi tiết */
  const goDetail = (navId) => {
    navigate(`/huong-dan-thu-tuc/thu-tuc/${navId}`);
  };

  /* Drag sheet để đóng */
  const handleDragEnd = (_e, info) => {
    if (info.offset.y > 120 || info.velocity.y > 700) onClose?.();
  };

  /* Ngăn cuộn sự kiện trên backdrop */
  const stopScroll = (e) => e.stopPropagation();

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end z-50"
      variants={backdrop}
      initial="hidden"
      animate="show"
      exit="exit"
      onWheel={stopScroll}
      onTouchMove={stopScroll}
      onClick={onClose}
    >
      <div
        className="bg-[#F2F2F2] w-full p-4 pr-0 shadow-lg rounded-t-3xl pb-[100px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Bottom Sheet */}
        <motion.div
          ref={sheetRef}
          className="flex flex-col justify-between items-center border-b pb-2 border-[#E1E5F3]"
          role="dialog"
          aria-modal="true"
          aria-label={`Thủ tục - ${category?.name ?? "Danh mục"}`}
          variants={sheet}
          initial="hidden"
          animate="show"
          exit="exit"
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.6 }}
          onDragEnd={handleDragEnd}
        >
          {/* Header */}
          <div className="flex w-full items-center justify-between border-b border-[#E1E5F3] pb-2 pr-4">
            <h2 className="line-clamp-1 text-lg font-bold">
              {category?.name || "Danh mục"}
            </h2>
            <button
              onClick={onClose}
              className="rounded p-1 focus:outline-none focus:ring-2 focus:ring-blue-300"
              aria-label="Đóng popup"
            >
              <img src="/icons/x-close.svg" alt="" className="h-4 w-4" />
            </button>
          </div>

          {/* Search */}
          <div className="relative w-full mt-3 flex items-center pr-4">
            <label htmlFor="popup-search" className="sr-only">
              Tìm kiếm thủ tục
            </label>
            <input
              id="popup-search"
              type="text"
              placeholder="Tìm kiếm thủ tục"
              className="w-full rounded-3xl border border-gray-200 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={searchTerm}
              autoComplete="off"
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && setSearchTerm((x) => x.trim())
              }
            />
            <span
              className="absolute right-6 cursor-pointer rounded p-1 focus:outline-none focus:ring-2 focus:ring-blue-300"
              onClick={() => setSearchTerm((x) => x.trim())}
              aria-label="Tìm kiếm"
            >
              <img src="/icons/search-black.svg" alt="" />
            </span>
          </div>

          {/* Content */}
          <div className="mt-3 w-full max-h-[70vh] overflow-auto pr-4">
            {serviceLoading ? (
              <div className="mt-6">
                {/* Nếu muốn giữ loader cũ: <Loading /> */}
                <ListSkeleton rows={3} />
              </div>
            ) : filteredProcedures.length ? (
              <motion.div
                initial="hidden"
                animate="show"
                variants={{
                  hidden: { opacity: 1 },
                  show: { opacity: 1, transition: { staggerChildren: 0.03 } },
                }}
                className="space-y-3"
              >
                {filteredProcedures.map((procedure) => (
                  <motion.button
                    key={procedure.nav_id}
                    type="button"
                    className="w-full rounded-lg border border-[#E1E5F3] bg-white p-3 text-left shadow-md transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    onClick={() => goDetail(procedure.nav_id)}
                    whileTap={{ scale: 0.99 }}
                    variants={{
                      hidden: { y: 6, opacity: 0 },
                      show: {
                        y: 0,
                        opacity: 1,
                        transition: { duration: 0.16 },
                      },
                    }}
                  >
                    <p className="border-b border-[#E1E5F3] pb-1 font-sf-medium text-[#1A1A1A]">
                      {procedure.category}
                    </p>

                    <div className="space-y-2 border-b border-[#E1E5F3] pb-2">
                      <div className="mt-2">
                        <p className="text-[13px] text-[#2E3A59]">
                          Tên thủ tục:
                        </p>
                        <p className="line-clamp-2 font-sf-medium text-[#0267FF]">
                          {procedure.ten}
                        </p>
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[13px] text-[#2E3A59]">
                            Thời gian nộp hồ sơ:
                          </p>
                          <p className="truncate text-[14px] font-sf-medium text-[#1A1A1A]">
                            {procedure.ngayNopHoSo}
                          </p>
                        </div>
                        <div className="min-w-0 text-right">
                          <p className="text-[13px] text-[#2E3A59]">
                            Thời gian thẩm định:
                          </p>
                          <p className="truncate text-[14px] font-sf-medium text-[#1A1A1A]">
                            {procedure.ngayThamDinh}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2 flex items-center justify-end text-[14px] text-[#0267FF] hover:text-blue-400">
                      Xem thêm &#8594;
                    </div>
                  </motion.button>
                ))}
              </motion.div>
            ) : (
              <p className="mt-4 text-center text-gray-500">
                Không tìm thấy kết quả phù hợp.
              </p>
            )}
          </div>
        </motion.div>
      </div>{" "}
    </motion.div>
  );
};

export default ProcedurePopup;
